import { Settings } from 'lucide-react';

import { SourcesProvider, SourcesAuthType } from '@api/types.gen';

import Github from '@/assets/ico/vendor/github.svg?c';
import Gitlab from '@/assets/ico/vendor/gitlab.svg?c';
import Bitbucket from '@/assets/ico/vendor/bitbucket.svg?c';
import Gitea from '@/assets/ico/vendor/gitea.svg?c';
import Azure from '@/assets/ico/vendor/azure.svg?c';

import { type IconType } from '@@/Icon';

export type ProviderConfig = {
  icon: IconType;
  title: string;
  description: string;
  fixedAuthType?: SourcesAuthType;
  usernameTooltip?: string;
  passwordLabel: string;
  passwordTooltip?: string;
};

export const providerConfigs: Record<SourcesProvider, ProviderConfig> = {
  [SourcesProvider.PROVIDER_GIT_HUB]: {
    icon: Github,
    title: 'GitHub',
    description:
      'Authenticate with a Personal Access Token. The token is used as the password in Basic Auth, this is how GitHub expects it.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_BASIC,
    usernameTooltip: 'Your GitHub username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A fine-grained or classic PAT with repo access.',
  },
  [SourcesProvider.PROVIDER_GIT_LAB]: {
    icon: Gitlab,
    title: 'GitLab',
    description:
      'Authenticate with a Personal Access Token. GitLab uses the token as the password in Basic Auth.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_BASIC,
    usernameTooltip: 'Your GitLab username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with read_repository or api scope.',
  },
  [SourcesProvider.PROVIDER_BITBUCKET_CLOUD]: {
    icon: Bitbucket,
    title: 'Bitbucket Cloud',
    description:
      'Authenticate with an App Password. Bitbucket Cloud uses App Passwords for Basic Auth.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_BASIC,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'App Password',
    passwordTooltip: 'A Bitbucket App Password with repository read access.',
  },
  [SourcesProvider.PROVIDER_BITBUCKET_DATA_CENTER]: {
    icon: Bitbucket,
    title: 'Bitbucket Data Center',
    description:
      'Authenticate with a Personal Access Token. Bitbucket Data Center uses the token in Bearer Token Auth.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_TOKEN,
    usernameTooltip: 'Your Bitbucket username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with repository read access.',
  },
  [SourcesProvider.PROVIDER_AZURE_DEV_OPS]: {
    icon: Azure,
    title: 'Azure DevOps',
    description:
      'Authenticate with a Personal Access Token. Azure DevOps uses Basic Authentication.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_BASIC,
    usernameTooltip: 'Your Azure DevOps username',
    passwordLabel: 'Personal Access Token',
    passwordTooltip: 'A PAT with Code (Read) permission.',
  },
  [SourcesProvider.PROVIDER_GITEA_FORGEJO]: {
    icon: Gitea,
    title: 'Gitea / Forgejo',
    description: 'Authenticate with an Access Token for Gitea or Forgejo.',
    fixedAuthType: SourcesAuthType.AUTH_TYPE_TOKEN,
    usernameTooltip: 'Your Gitea/Forgejo username',
    passwordLabel: 'Access Token',
    passwordTooltip: 'An API access token with repository read access.',
  },
  [SourcesProvider.PROVIDER_CUSTOM]: {
    icon: Settings,
    title: 'Custom',
    description:
      'Configure authentication manually for any Git provider. Choose Basic or Token auth.',
    usernameTooltip: 'Your Git username',
    passwordLabel: 'Password',
  },
};
